import { useMemo, useState, useCallback, useEffect } from 'react'
import { useApplication } from 'src/context/application'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
} from 'src/components/application/defaultComponents'
import ApplicationPrebuiltFooter from 'src/components/application/PrebuiltBodyFooter'
import BasicForm from 'src/components/application/judges/newBusiness/BasicForm'
import { ValidationError } from 'yup'
import ApplicationData from 'src/dtos/applicationData'
import { JudgeInfoSchema } from 'src/yup/NewAppSchema'

const Basic = () => {
  const { application } = useApplication()
  const [basicSection, setBasicSection] = useState(application?.judgeApplicationData?.basicSection 
    ?? {contactInfo: {isSameAsJudge: false}})
  const [firmSection, setFirmSection] = useState(application?.firmSection ?? {})
  const [validatedFields, setValidatedFields] = useState<string[]>([])
  const applicantPrimaryInfo = application.applicant

  
  const validateSteps = useMemo(() => {
    const totalSections = 10
    let steps: {[key: string]: boolean} = {}
    try {
      JudgeInfoSchema.validateSync(basicSection, { abortEarly: false })

      for (let stepNumber = 1; stepNumber <= totalSections; stepNumber++) {
        steps[`step${stepNumber}`] = true
      }

      return steps
    } catch(err) {
      const { errors } = err as ValidationError

      for (let stepNumber = 1; stepNumber <= totalSections; stepNumber++) {
        steps[`step${stepNumber}`] = !errors.includes(`step${stepNumber}`)
      }

      return steps
    }
  }, [basicSection, JudgeInfoSchema])

  const updatedApplicationData = (): ApplicationData => ({
    ...application,
    currentPage: window.location.pathname,
    firmSection: {
      ...firmSection,
      primaryContact: {
        firstName: applicantPrimaryInfo?.firstName,
        lastName: applicantPrimaryInfo?.lastName,
        email: applicantPrimaryInfo?.emailAddress,
        isFirmAdministrator: applicantPrimaryInfo?.isFirmAdministrator
      }
    },
    judgeApplicationData: {
      ...application?.judgeApplicationData,
      basicSection: {
        ...application?.judgeApplicationData?.basicSection,
        ...basicSection
      }      
    }
  })

  const handleValidateForm = useCallback(() => {
    try {
      JudgeInfoSchema.validateSync(basicSection, { abortEarly: false })
      setValidatedFields([])
    } catch(err) {
      const { errors } = err as ValidationError
      const updatedErrorArray = Array.from(new Set(errors))

      if(JSON.stringify(validatedFields) !== JSON.stringify(updatedErrorArray)) {
        setValidatedFields(updatedErrorArray)
        if(updatedErrorArray.length > 0) {
          document.getElementById(updatedErrorArray[0])?.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }
  }, [basicSection, JudgeInfoSchema, validatedFields])

  useEffect(() => {
    if(Object.keys(validatedFields).length > 0) {
      handleValidateForm()
    }
  }, [handleValidateForm, validatedFields])

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle>Let’s start with your basic information.</DefaultTitle>
        <DefaultSubtitle>
          Please fill out the information below for the Judge that is the applicant.
        </DefaultSubtitle>
        <BasicForm
          judgeBasicData={basicSection}
          setJudgeBasicData={setBasicSection}
          validateSteps={validateSteps}
          validatedFields={validatedFields}
        />
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/new"
        isFormValid={Object.keys(validateSteps).every(key => validateSteps[key] === true)}
        nextRoute="/judge/insurance/last-insurances"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleValidateForm}
      />
    </ApplicationBodyContainer>
  )
}

export default Basic